/**
 * Read the identity this person already proved, from anywhere.
 *
 * The point of this hook is that a screen never has a reason to ask for a
 * national ID twice. Before it existed, `verification/license.tsx` inlined its
 * own Supabase query inside the component to get the number, and the payout
 * form simply asked the host to type it again.
 */
import { useQuery } from '@tanstack/react-query'
import { identityService, type VerifiedIdentity } from '../services/identity.service'
import { useAuth } from './use-auth'

export const identityKeys = {
  all: ['identity'] as const,
  verified: (profileId: string) => [...identityKeys.all, 'verified', profileId] as const,
  latestAttempt: (profileId: string) => [...identityKeys.all, 'attempt', profileId] as const,
}

/**
 * The verified identity for the signed-in person, or null if they have none.
 *
 * @public
 */
export function useVerifiedIdentity() {
  const { user } = useAuth()
  const profileId = user?.id ?? ''

  return useQuery<VerifiedIdentity | null>({
    queryKey: identityKeys.verified(profileId),
    queryFn: () => identityService.getVerifiedIdentity(profileId),
    enabled: Boolean(profileId),
    // An identity changes when a verification completes, which is rare and
    // already invalidates through the verification overview channel.
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * The last attempt, approved or not — what the person typed, so a retry does
 * not start from an empty field.
 *
 * @public
 */
export function useLatestIdentityAttempt() {
  const { user } = useAuth()
  const profileId = user?.id ?? ''

  return useQuery<VerifiedIdentity | null>({
    queryKey: identityKeys.latestAttempt(profileId),
    queryFn: () => identityService.getLatestAttempt(profileId),
    enabled: Boolean(profileId),
    // Deliberately fresher than the verified read: this one exists for the
    // moment right after a failure, when the row was written seconds ago.
    staleTime: 0,
  })
}
